/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, useRef, useEffect } from 'react';
import ReviewCard from './components/ReviewCard';
import ReviewStats from './components/ReviewStats';
import SubmitReviewModal from './components/SubmitReviewModal';
import ReviewsQuickLinks from './components/ReviewsQuickLinks';
import { useReviews } from '../../hooks/useReviews';
import { useReviewsPageSchema } from '../../hooks/useReviewsSchema';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

function ShareButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const pageUrl = 'https://rockhoundauto.com/reviews';
  const shareText = 'Check out Rockhound Auto Sales reviews — honest used cars on the Oregon Coast!';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareOptions = [
    {
      label: 'Share on Facebook',
      icon: 'ri-facebook-fill',
      color: 'text-[#1877F2]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    },
    {
      label: 'Share on X (Twitter)',
      icon: 'ri-twitter-x-fill',
      color: 'text-[#1C2B1E]',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      label: 'Share via Text / SMS',
      icon: 'ri-message-2-line',
      color: 'text-green-600',
      href: `sms:?body=${encodeURIComponent(shareText + ' ' + pageUrl)}`,
    },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="px-6 sm:px-8 py-3 sm:py-4 bg-[#E8B84B] text-[#1C2B1E] rounded-full font-semibold hover:bg-[#d4a43a] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-share-forward-line" aria-hidden="true"></i>
        Share This Page
      </button>

      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-[#E8E2D6] overflow-hidden z-50">
          <div className="px-4 py-2.5 border-b border-[#E8E2D6]">
            <p className="text-xs font-semibold text-[#4A5568] uppercase tracking-widest">Spread the word</p>
          </div>
          {shareOptions.map((opt) => (
            <a
              key={opt.label}
              href={opt.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[#F7F5F0] transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 flex items-center justify-center" aria-hidden="true">
                <i className={`${opt.icon} text-lg ${opt.color}`}></i>
              </div>
              <span className="text-sm font-medium text-[#1C2B1E]">{opt.label}</span>
            </a>
          ))}
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F5F0] transition-colors cursor-pointer border-t border-[#E8E2D6]"
          >
            <div className="w-7 h-7 flex items-center justify-center" aria-hidden="true">
              <i className={`${copied ? 'ri-check-line text-green-600' : 'ri-link text-[#4A5568]'} text-lg`}></i>
            </div>
            <span className="text-sm font-medium text-[#1C2B1E]">
              {copied ? 'Link Copied!' : 'Copy Link'}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function ReviewsPage() {
  const { reviews, loading, aggregate } = useReviews();
  
  // Inject comprehensive schema with individual reviews and breadcrumbs
  useReviewsPageSchema(reviews, aggregate);
  
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'highest' | 'lowest'>('recent');

  // ✅ useSEOHead with updated title
  useSEOHead({
    title: 'Rockhound Auto Reviews | Warrenton OR Used Car Dealer',
    description: 'Read real customer reviews of Rockhound Auto in Warrenton, Oregon. See why Oregon Coast buyers trust us for honest used car sales.',
    keywords: 'Rockhound Auto reviews, Warrenton car dealer reviews, Oregon Coast used car reviews',
    canonicalUrl: 'https://rockhoundauto.com/reviews',
  });

  // Filter and sort reviews
  let filteredReviews = filterRating
    ? reviews.filter((r) => r.rating === filterRating)
    : reviews;

  filteredReviews = [...filteredReviews].sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else if (sortBy === 'highest') {
      return b.rating - a.rating;
    } else {
      return a.rating - b.rating;
    }
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Reviews',
          item: 'https://rockhoundauto.com/reviews',
        },
      ],
    };

    injectJsonLd('schema-reviews-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-reviews-breadcrumb');
    };
  }, []);

  useEffect(() => {
    injectFAQSchema(
      'schema-reviews-faq',
      pageFaqs.reviews,
      'https://rockhoundauto.com/reviews#faqpage'
    );

    return () => {
      removeFAQSchema('schema-reviews-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Reviews' }]} />
      {/* Hero Section */}
      <div className="bg-[#F7F5F0] border-b border-[#E8E2D6] pt-16 lg:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-['Oswald'] text-3xl sm:text-5xl font-bold text-[#1C2B1E] mb-4">
              Customer Reviews
            </h1>
            <p className="text-base sm:text-lg text-[#374151] mb-3">
              Real stories from real customers. See why people choose Rockhound Auto Sales to <a href="/inventory" className="text-[#1E3A5F] hover:text-[#E8B84B] underline font-medium">find their next vehicle</a> and <a href="/write-a-review" className="text-[#1E3A5F] hover:text-[#E8B84B] underline font-medium">share your experience</a>.
            </p>
            <p className="text-sm text-[#4A5568] mb-6 sm:mb-8">
              Customers from <a href="/service-areas/astoria" className="text-[#1E3A5F] hover:text-[#E8B84B] underline font-medium">Astoria</a>, <a href="/service-areas/seaside" className="text-[#1E3A5F] hover:text-[#E8B84B] underline font-medium">Seaside</a>, and <a href="/used-cars-sw-washington" className="text-[#1E3A5F] hover:text-[#E8B84B] underline font-medium">SW Washington</a> have trusted us with their car purchase — read what they said.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => setShowSubmitModal(true)}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line" aria-hidden="true"></i>
                Write a Review
              </button>
              <a
                href="https://share.google/vqOn8ooyXd5MkpaOe"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white border-2 border-[#1C2B1E] text-[#1C2B1E] rounded-full font-medium hover:bg-[#F7F5F0] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-google-fill text-[#E8B84B]" aria-hidden="true"></i>
                Review Us on Google
              </a>
              <ShareButton />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white border-b border-[#E8E2D6] py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ReviewStats aggregate={aggregate} loading={loading} />
        </div>
      </div>

      {/* Be Our First Reviewer Banner — shown when no reviews exist */}
      {!loading && reviews.length === 0 && (
        <>
          <div className="bg-[#1C2B1E] py-10 sm:py-14">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 bg-[#E8B84B]/20 text-[#E8B84B] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
                <i className="ri-star-line" aria-hidden="true"></i>
                New Dealership
              </div>
              <h2 className="font-['Oswald'] text-3xl sm:text-4xl font-bold text-white mb-4">
                Be Our First Reviewer
              </h2>
              <p className="text-[#A8B8AA] text-base sm:text-lg mb-3 max-w-2xl mx-auto leading-relaxed">
                We&apos;re brand new to Warrenton, OR — and we&apos;re building our reputation one honest deal at a time.
                If you&apos;ve visited us, test-driven a vehicle, or bought from us, your review means the world to us
                and helps other Oregon Coast &amp; SW Washington buyers find a dealership they can trust.
              </p>
              <p className="text-[#8AA88E] text-sm mb-8">
                Takes less than 2 minutes · No account required on Google
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://share.google/vqOn8ooyXd5MkpaOe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-[#E8B84B] text-[#1C2B1E] rounded-full font-bold hover:bg-[#d4a43a] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-google-fill text-lg" aria-hidden="true"></i>
                  Leave a Google Review
                </a>
                <a
                  href="/write-a-review"
                  className="px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-full font-medium hover:border-white/60 hover:bg-white/5 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-edit-line" aria-hidden="true"></i>
                  Write a Review on Our Site
                </a>
              </div>
              {/* Trust micro-copy */}
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto list-none p-0">
                {[
                  { icon: 'ri-shield-check-line', text: 'Honest, no-pressure sales' },
                  { icon: 'ri-price-tag-3-line', text: 'Transparent pricing always' },
                  { icon: 'ri-heart-line', text: 'Every sale supports Oregon Dog Rescue' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center justify-center gap-2 text-[#A8B8AA] text-xs">
                    <i className={`${item.icon} text-[#E8B84B] text-base`} aria-hidden="true"></i>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* First Review Milestone Progress Bar */}
          <div className="bg-white border-b border-[#E8E2D6] py-8 sm:py-10">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-[#E8B84B]/10 text-[#7a5208] text-xs font-bold px-4 py-1.5 rounded-full mb-3 uppercase tracking-widest">
                  <i className="ri-trophy-line" aria-hidden="true"></i>
                  First Milestone
                </div>
                <h2 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#1C2B1E] mb-1">
                  Help Us Reach 10 Reviews!
                </h2>
                <p className="text-[#4A5568] text-sm">
                  Every review builds trust for future buyers on the Oregon Coast &amp; SW Washington.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="bg-[#F7F5F0] rounded-2xl border border-[#E8E2D6] p-6 sm:p-8">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <span className="font-['Oswald'] text-4xl font-bold text-[#1C2B1E]">0</span>
                    <span className="text-[#4A5568] text-lg font-medium"> / 10 reviews</span>
                  </div>
                  <span className="text-xs text-[#4A5568] font-medium pb-1">0% complete</span>
                </div>

                {/* Track */}
                <div
                  className="relative h-4 bg-[#E8E2D6] rounded-full overflow-hidden mb-4"
                  role="progressbar"
                  aria-valuenow={0}
                  aria-valuemin={0}
                  aria-valuemax={10}
                  aria-label="Review milestone progress: 0 of 10 reviews"
                >
                  <div
                    className="h-full bg-gradient-to-r from-[#E8B84B] to-[#d4a43a] rounded-full transition-all duration-700"
                    style={{ width: '0%' }}
                  ></div>
                  {[25, 50, 75].map((pct) => (
                    <div
                      key={pct}
                      aria-hidden="true"
                      className="absolute top-0 bottom-0 w-0.5 bg-white/60"
                      style={{ left: `${pct}%` }}
                    ></div>
                  ))}
                </div>

                {/* Milestone labels */}
                <div className="flex justify-between text-xs text-[#4A5568] mb-6" aria-hidden="true">
                  <span>0</span>
                  <span>3</span>
                  <span>5</span>
                  <span>8</span>
                  <span className="font-semibold text-[#92650a]">🏆 10</span>
                </div>

                {/* Reward callout */}
                <div className="bg-[#1C2B1E]/5 border border-[#1C2B1E]/10 rounded-xl p-4 flex items-start gap-3 mb-6">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <i className="ri-gift-line text-[#E8B84B] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1C2B1E] mb-0.5">When we hit 10 reviews…</p>
                    <p className="text-xs text-[#4A5568] leading-relaxed">
                      We&apos;ll donate an extra $100 to <strong>Oregon Dog Rescue</strong> in honor of our first 10 reviewers.
                      Your words help dogs find homes too.
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://share.google/vqOn8ooyXd5MkpaOe"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-3 bg-[#1C2B1E] text-white rounded-full font-semibold hover:bg-[#2d4430] transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-google-fill text-[#E8B84B]" aria-hidden="true"></i>
                    Be Reviewer #1 on Google
                  </a>
                  <a
                    href="/write-a-review"
                    className="flex-1 py-3 bg-white border-2 border-[#1C2B1E] text-[#1C2B1E] rounded-full font-semibold hover:bg-[#F7F5F0] transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-edit-line" aria-hidden="true"></i>
                    Write on Our Site
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filters & Reviews */}
      <div className="bg-[#F7F5F0] min-h-screen py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="bg-white rounded-2xl border border-[#E8E2D6] p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              {/* Rating Filter */}
              <fieldset className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 border-0 p-0 m-0">
                <legend className="text-sm font-medium text-[#1C2B1E] mb-1 sm:mb-0 sm:mr-1">Filter by rating:</legend>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilterRating(null)}
                    aria-pressed={filterRating === null}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      filterRating === null
                        ? 'bg-[#1C2B1E] text-white'
                        : 'bg-[#F7F5F0] text-[#374151] hover:bg-[#E8E2D6]'
                    }`}
                  >
                    All
                  </button>
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setFilterRating(rating)}
                      aria-pressed={filterRating === rating}
                      aria-label={`Filter by ${rating} stars`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                        filterRating === rating
                          ? 'bg-[#1C2B1E] text-white'
                          : 'bg-[#F7F5F0] text-[#374151] hover:bg-[#E8E2D6]'
                      }`}
                    >
                      {rating}
                      <i className="ri-star-fill text-[#E8B84B]" aria-hidden="true"></i>
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Sort */}
              <div className="flex items-center gap-2">
                <label htmlFor="reviews-sort" className="text-sm font-medium text-[#1C2B1E]">Sort by:</label>
                <select
                  id="reviews-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'recent' | 'highest' | 'lowest')}
                  className="px-4 py-2 bg-[#F7F5F0] border border-[#E8E2D6] rounded-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent"
                >
                  <option value="recent">Most Recent</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reviews List */}
          {loading ? (
            <div role="status" aria-live="polite" className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#E8B84B] border-t-transparent" aria-hidden="true"></div>
              <p className="mt-4 text-[#374151]">Loading reviews...</p>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8E2D6] p-8 sm:p-12 text-center">
              <i className="ri-star-line text-6xl text-gray-300 mb-4" aria-hidden="true"></i>
              <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-2">
                No reviews found
              </h2>
              <p className="text-[#374151] mb-6">
                {filterRating ? 'Try selecting a different rating filter' : 'Be the first to leave a review!'}
              </p>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="px-6 py-3 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line" aria-hidden="true"></i>
                Write a Review
              </button>
            </div>
          ) : (
            <div role="feed" aria-label="Customer reviews" className="space-y-4 sm:space-y-6">
              {filteredReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-8 sm:mt-12 bg-white rounded-2xl border border-[#E8E2D6] p-6 sm:p-8 text-center">
            <h3 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#1C2B1E] mb-4">
              Share Your Experience
            </h3>
            <p className="text-[#374151] mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              Your feedback helps us improve and helps other customers make informed decisions. We&apos;d love to hear about your experience!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowSubmitModal(true)}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line" aria-hidden="true"></i>
                Write a Review
              </button>
              <a
                href="https://share.google/vqOn8ooyXd5MkpaOe"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white border-2 border-[#1C2B1E] text-[#1C2B1E] rounded-full font-medium hover:bg-[#F7F5F0] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-google-fill text-[#E8B84B]" aria-hidden="true"></i>
                Review Us on Google
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links Section */}
      <ReviewsQuickLinks />

      {/* Submit Review Modal */}
      {showSubmitModal && <SubmitReviewModal onClose={() => setShowSubmitModal(false)} />}
    </>
  );
}